/**
 * TalepGecmisi.jsx — the activity history of a stand's requests: previous value, new value, when, who (backlog #453, 2026-10-17; backend #452).
 *
 * Three places mount it: the company's stand request page (FirmaTalepStandDetailPage, tab "Geçmiş"), the organizer's Talepler row (a modal), and the staff
 * stand detail's Talepler tab (StandDetayPage). A staff request detail page of its own (#75) does not exist yet: it can mount this the same way.
 * Whoever may read the stand's requests may read this; the backend decides what a viewer sees (a changer's name only for staff, the changer himself and his own
 * company; everyone else sees the side and the company). Nothing here writes.
 *
 * The history is kept from the day the backend feature was deployed: earlier changes were never recorded, and the empty state says so.
 *
 * @param {number|string} standId        the stand (the numeric id; anything else shows the empty state and calls nothing)
 * @param {string}        [tur]          narrow to one kind from the start: "TEKNIK" or "REQUEST" (default: both)
 * @param {string}        [kategori]     narrow to one category (the contract's categoryKey; needs `tur`)
 * @param {boolean}       [filtreGoster] show the kind chips (default true; false when the caller fixed the kind)
 * @param {number}        [yenileJetonu] changes when the caller saved something: the list is read again
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import { getTalepGecmisi } from "../../services/standTalepService";
import {
  GECMIS_TURLERI, ISLEM_RENK, TARAF_RENK, degerBicimi, alanAdi, kategoriAdi, yapanBilgisi, olayZamani, siraliDegisiklikler,
} from "./talepGecmisi";

const FILTRELER = [
  { tur: GECMIS_TURLERI.HEPSI, anahtar: "hepsi" },
  { tur: GECMIS_TURLERI.TEKNIK, anahtar: "teknik" },
  { tur: GECMIS_TURLERI.TALEP, anahtar: "talep" },
];

function Deger({ alan, deger, t, yerel, eski = false }) {
  const { metin, tam } = degerBicimi(alan, deger, t, yerel);
  const bosMu = deger === null || deger === undefined || String(deger).trim() === "";
  return (
    <span
      className={eski ? "text-muted text-decoration-line-through" : bosMu ? "text-muted" : "fw-semibold"}
      title={tam || undefined}
      style={{ overflowWrap: "anywhere" }}
    >
      {metin}
    </span>
  );
}

function Olay({ olay, t, yerel }) {
  const yapan = yapanBilgisi(olay, t);
  const degisiklikler = siraliDegisiklikler(olay.degisiklikler);
  const yeniKayit = olay.islem === "CREATE";
  return (
    <div className="border-bottom py-2" data-testid="gecmis-olay">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        <Badge color={ISLEM_RENK[olay.islem] ?? "light"} className={ISLEM_RENK[olay.islem] ? undefined : "text-dark"}>
          {t(`talepGecmisi.islem.${olay.islem}`)}
        </Badge>
        <span className="fw-semibold">{kategoriAdi(olay, t)}</span>
        <span className="text-muted small ms-auto">{olayZamani(olay.tarih, yerel)}</span>
      </div>
      <div className="small mb-1">
        <Badge color={TARAF_RENK[olay.taraf] ?? "light"} className={TARAF_RENK[olay.taraf] ? "me-1" : "me-1 text-dark"}>{yapan.taraf}</Badge>
        {yapan.ad && <span className="fw-semibold me-1">{yapan.ad}</span>}
        {yapan.firma && <span className="text-muted">{yapan.firma}</span>}
      </div>
      <ul className="list-unstyled small mb-0">
        {degisiklikler.map((d) => (
          <li key={d.alan} className="d-flex flex-wrap gap-1" data-testid="gecmis-degisiklik">
            <span className="text-muted">{alanAdi(d.alan, t)}:</span>
            {yeniKayit ? (
              <Deger alan={d.alan} deger={d.yeni} t={t} yerel={yerel} />
            ) : (
              <>
                <Deger alan={d.alan} deger={d.eski} t={t} yerel={yerel} eski />
                <span aria-hidden="true">→</span>
                <Deger alan={d.alan} deger={d.yeni} t={t} yerel={yerel} />
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TalepGecmisi({ standId, tur = "", kategori = "", filtreGoster = true, yenileJetonu = 0 }) {
  const { t } = useTranslation("portal");
  const yerel = currentLocaleTag();
  const [secilenTur, setSecilenTur] = useState(tur || GECMIS_TURLERI.HEPSI);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [yenile, setYenile] = useState(0);

  const gecerli = /^\d+$/.test(String(standId ?? ""));
  // the category belongs to the kind the caller fixed: a different kind chosen here shows every category of that kind
  const etkinKategori = secilenTur === (tur || GECMIS_TURLERI.HEPSI) ? kategori : "";

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getTalepGecmisi(standId, { tur: secilenTur, kategori: etkinKategori });
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (gecerli) run();
    return () => { ignore = true; };
  }, [standId, gecerli, secilenTur, etkinKategori, yenileJetonu, yenile]);

  const olaylar = veri?.olaylar ?? [];

  return (
    <div data-testid="talep-gecmisi">
      <p className="small text-muted mb-2">{t("talepGecmisi.aciklama")}</p>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {filtreGoster && FILTRELER.map((f) => (
          <Button
            key={f.anahtar} size="sm" color={secilenTur === f.tur ? "primary" : "light"}
            onClick={() => setSecilenTur(f.tur)} data-testid={`gecmis-filtre-${f.anahtar}`}
          >
            {t(`talepGecmisi.filtre.${f.anahtar}`)}
          </Button>
        ))}
        {gecerli && (
          <Button size="sm" color="link" className="ms-auto p-0" onClick={() => setYenile((n) => n + 1)} disabled={yukleniyor}>
            {t("talepGecmisi.yenile")}
          </Button>
        )}
      </div>

      {!gecerli && <p className="text-muted small mb-0" data-testid="gecmis-gecersiz">{t("talepGecmisi.standYok")}</p>}
      {gecerli && yukleniyor && <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>}
      {gecerli && hata && <Alert color="danger" className="small py-2">{t("talepGecmisi.hata", { mesaj: hata })}</Alert>}
      {gecerli && !yukleniyor && !hata && olaylar.length === 0 && (
        <p className="text-muted small mb-0" data-testid="gecmis-bos">{t("talepGecmisi.bos")}</p>
      )}
      {gecerli && !hata && olaylar.map((o) => <Olay key={o.grup} olay={o} t={t} yerel={yerel} />)}
    </div>
  );
}
